import { useEffect, useRef, useState } from "react";
import { Seo, orgJsonLd, websiteJsonLd, breadcrumbJsonLd, localBusinessJsonLd, serviceJsonLd, itemListJsonLd } from "@/components/Seo";
import { Link } from "react-router-dom";
import { trackPublicCta } from "@/lib/track";
import {
  ArrowRight,
  Battery,
  Boxes,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Container,
  CheckCircle2,
  XCircle,
  Wine,
  Box,
  Apple,
  ShoppingBag,
  Droplet,
  Factory,
  Flame,
  GraduationCap,
  Headset,
  Layers,
  Leaf,
  MapPin,
  Newspaper,
  Package,
  Recycle,
  Sparkles,
  Route,
  Scale,
  ScanLine,
  ShieldCheck,
  Trash2,
  Truck,
  Users,
  Warehouse,
  Wrench,
} from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useReveal } from "@/hooks/useReveal";
import { CountUp } from "@/components/CountUp";
import { VideoHeroBackground } from "@/components/VideoHeroBackground";
import { HomepageNews } from "@/components/HomepageNews";
import cityOakland from "@/assets/city-oakland.webp";
import citySanJose from "@/assets/city-sanjose.webp";
import commercialProperty from "@/assets/commercial-property.webp";
import opsFacility from "@/assets/ops-facility.webp";

import cwsRecyclingBin from "@/assets/cws-recycling-bin.webp";
import imgPaper from "@/assets/recycle-paper.webp";
import imgPlasticBottlesAsset from "@/assets/recycle-plastic-bottles.png.asset.json";
const imgPlasticBottles = imgPlasticBottlesAsset.url;
import imgGlassJars from "@/assets/recycle-glass-jars.webp";
import imgMetalCans from "@/assets/recycle-metal-cans.webp";
import imgPlasticBags from "@/assets/recycle-plastic-bags.webp";
import imgFoodWaste from "@/assets/recycle-food-waste.webp";
import imgBatteries from "@/assets/recycle-batteries.webp";
import imgTrash from "@/assets/recycle-trash.webp";

import svcCollection from "@/assets/cws-recycling-collection-truck-fleet.webp";
import svcMrf from "@/assets/cws-materials-recovery-facility-sorting-line.webp";
import svcCommodity from "@/assets/cws-baled-cardboard-recovered-commodities.webp";
import svcCommercial from "@/assets/cws-commercial-recycling-dumpster-cart-oakland.webp";
import svcBuyback from "@/assets/cws-buyback-dropoff-cardboard-newspaper-cans.webp";
import svcInfrastructure from "@/assets/vietnam-waste-solutions-facility-aerial.webp";
const newsCompanyVideo = "/__l5e/assets-v1/02ac78d3-ffe7-4320-b246-3955265c3e21/news-company.mp4";
import newsServiceUpdates from "@/assets/news-service-updates.webp";
import newsPublicNotices from "@/assets/news-public-notices.webp";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const heroVideo = "/__l5e/assets-v1/7d75e61d-ffde-444b-bf92-388dc01e4259/hero-cws-0328.mp4";
const heroPoster = "/videos/hero-cws-0328-poster.webp";
const aboutCwsOperationsVideo = "/__l5e/assets-v1/aa52721d-c23e-41ca-95d2-ae5e2f3e5d99/about-cws-operations.mp4";
const aboutCwsOperationsPoster = "/videos/about-cws-operations-poster.webp";

/* ------------------------------------------------------------------
   Shared section primitives
   ------------------------------------------------------------------ */
const Eyebrow = ({ children, light = false }: { children: React.ReactNode; light?: boolean }) => (
  <span className={`eyebrow-row ${light ? "!text-white/85" : ""}`}>{children}</span>
);

const SectionTitle = ({
  children,
  light = false,
  className = "",
}: {
  children: React.ReactNode;
  light?: boolean;
  className?: string;
}) => (
  <h2
    className={`mt-3 font-extrabold tracking-tight text-balance ${light ? "text-white" : "text-charcoal"} ${className}`}
    style={{ fontSize: "clamp(1.75rem, 1.1rem + 2vw, 2.75rem)", lineHeight: 1.08, letterSpacing: "-0.018em" }}
  >
    {children}
  </h2>
);

const SectionLead = ({
  children,
  light = false,
  className = "",
}: {
  children: React.ReactNode;
  light?: boolean;
  className?: string;
}) => (
  <p className={`mt-4 lead max-w-2xl ${light ? "text-white/80" : "text-muted-foreground"} ${className}`}>{children}</p>
);

/* ------------------------------------------------------------------
   Recycling Stage, interactive items + bin
   ------------------------------------------------------------------ */
const ACCEPTED_ITEMS = [
  { img: imgPaper, label: "Paper & Cardboard" },
  { img: imgPlasticBottles, label: "Plastic Bottles" },
  { img: imgGlassJars, label: "Glass Jars" },
  { img: imgMetalCans, label: "Metal Cans" },
];
const REJECTED_ITEMS = [
  { img: imgPlasticBags, label: "Plastic Bags" },
  { img: imgFoodWaste, label: "Food Waste" },
  { img: imgBatteries, label: "Batteries" },
  { img: imgTrash, label: "Trash" },
];

type StageItemProps = {
  img: string;
  label: string;
  kind: "yes" | "no";
  side: "left" | "right";
  index: number;
  active: boolean;
  onActivate: () => void;
  binPulse: () => void;
};

const StageItem = ({ img, label, kind, side, index, active, onActivate, binPulse }: StageItemProps) => {
  const isYes = kind === "yes";
  // Animation direction toward the bin (center)
  const flyX = side === "left" ? "120%" : "-120%";
  const flyRot = side === "left" ? "22deg" : "-22deg";

  const handle = () => {
    onActivate();
    if (isYes) binPulse();
  };

  return (
    <button
      type="button"
      onClick={handle}
      onMouseEnter={handle}
      aria-label={`${label}, ${isYes ? "goes in cart" : "keep out"}`}
      style={
        {
          transitionDelay: `${index * 90}ms`,
          ["--fly-x" as string]: flyX,
          ["--fly-rot" as string]: flyRot,
        } as React.CSSProperties
      }
      className="reveal stage-card group relative flex w-full items-center gap-1.5 rounded-xl border border-border bg-white/90 p-1.5 text-left shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-transparent hover:shadow-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 sm:gap-3 sm:rounded-2xl sm:p-3 md:p-3.5"
    >
      {/* Gradient border glow on hover */}
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${
          isYes
            ? "bg-gradient-to-br from-accent/20 via-primary/10 to-transparent ring-1 ring-accent/40"
            : "bg-gradient-to-br from-destructive/20 via-destructive/5 to-transparent ring-1 ring-destructive/40"
        }`}
      />

      {/* Image disc */}
      <span className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center sm:h-16 sm:w-16 md:h-20 md:w-20">
        <span
          aria-hidden
          className={`absolute inset-0 rounded-full blur-xl transition-opacity duration-300 ${
            isYes ? "bg-accent/35" : "bg-destructive/30"
          } opacity-40 group-hover:opacity-100`}
        />
        <span
          aria-hidden
          className={`absolute inset-1 rounded-full ${
            isYes ? "bg-accent-soft" : "bg-destructive/10"
          }`}
        />
        <img width={1000} height={1000} decoding="async"
          key={active ? `${label}-a` : `${label}-i`}
          src={img}
          alt={label}
          loading="lazy"
          draggable={false}
          className={`relative h-8 w-8 object-contain drop-shadow-md transition-transform duration-300 sm:h-12 sm:w-12 md:h-16 md:w-16 ${
            active ? (isYes ? "stage-fly-yes" : "stage-shake-no") : "group-hover:scale-110 group-hover:-translate-y-0.5"
          }`}
        />
        {/* Status badge */}
        <span
          aria-hidden
          className={`absolute -right-1 -top-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-white shadow-md ring-2 ring-white transition-all duration-300 ${
            isYes ? "bg-accent" : "bg-destructive"
          } scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100`}
        >
          {isYes ? <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.6} /> : <XCircle className="h-3.5 w-3.5" strokeWidth={2.6} />}
        </span>
      </span>

      {/* Label + tag */}
      <span className="relative min-w-0 flex-1">
        <span className="block truncate font-display text-[11px] font-bold leading-tight text-charcoal sm:text-[13px] md:text-[14.5px]">
          {label}
        </span>
        <span
          className={`mt-0.5 hidden items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] sm:inline-flex sm:text-[11px] ${
            isYes ? "text-accent" : "text-destructive"
          }`}
        >
          {isYes ? "Goes in" : "Keep out"}
        </span>
      </span>

      {/* Trail line toward bin (desktop only) */}
      <span
        aria-hidden
        className={`pointer-events-none absolute top-1/2 hidden h-[2px] -translate-y-1/2 rounded-full bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:block ${
          side === "left"
            ? "left-full ml-1 w-10 origin-left from-accent/80 to-transparent"
            : "right-full mr-1 w-10 origin-right from-transparent to-accent/80"
        } ${!isYes ? (side === "left" ? "from-destructive/70" : "to-destructive/70") : ""}`}
      />
    </button>
  );
};

const RecyclingStage = () => {
  const [active, setActive] = useState<string | null>(null);
  const [pulse, setPulse] = useState(0);
  const [animate, setAnimate] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const triggerPulse = () => setPulse((p) => p + 1);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (!("IntersectionObserver" in window)) { setAnimate(true); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setAnimate(e.isIntersecting)),
      { rootMargin: "100px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Shared bin block (used in both mobile-stacked and desktop-grid layouts)
  const Bin = (
    <div className="relative flex flex-col items-center">
      {/* Halo ring */}
      <div
        aria-hidden
        key={`halo-${pulse}`}
        className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-primary/25 via-accent/15 to-transparent blur-2xl bin-pulse sm:h-80 sm:w-80"
      />
      {/* Rotating dashed rings (desktop) */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[340px] w-[340px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-primary/20 lg:block"
        style={animate ? { animation: "spin 40s linear infinite" } : undefined}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dotted border-accent/15 lg:block"
        style={animate ? { animation: "spin 60s linear infinite reverse" } : undefined}
      />

      <div className="relative w-full max-w-[200px] sm:max-w-[260px] lg:max-w-[340px]">
        <img width={1254} height={1254} decoding="async"
          src={cwsRecyclingBin}
          alt="California Waste Solutions blue recycling bin"
          loading="lazy"
          draggable={false}
          key={`bin-${pulse}`}
          className="relative z-10 mx-auto h-auto w-full select-none drop-shadow-2xl bin-bounce"
        />
        <div
          aria-hidden
          className="absolute inset-x-10 -bottom-1 z-0 h-6 rounded-full bg-charcoal/30 blur-xl"
        />
        <span
          aria-hidden
          key={`spk-${pulse}`}
          className="pointer-events-none absolute left-1/2 top-1/3 z-20 -translate-x-1/2 text-accent sparkle-pop"
        >
          <Sparkles className="h-7 w-7 drop-shadow" />
        </span>
      </div>

      <div className="relative z-10 mt-4 text-center sm:mt-5">
        <p className="font-display text-[13px] font-extrabold uppercase tracking-[0.2em] text-primary sm:text-sm">
          CWS Recycling Cart
        </p>
      </div>
    </div>
  );

  const AcceptedHeader = (
    <div className="flex items-center justify-center">
      <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-accent shadow-sm">
        <CheckCircle2 className="h-3.5 w-3.5" /> Goes in the cart
      </span>
    </div>
  );
  const RejectedHeader = (
    <div className="flex items-center justify-center">
      <span className="inline-flex items-center gap-2 rounded-full border border-destructive/40 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-destructive shadow-sm">
        <XCircle className="h-3.5 w-3.5" /> Never in the cart
      </span>
    </div>
  );

  const acceptedItems = (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-1">
      {ACCEPTED_ITEMS.map((it, i) => (
        <StageItem
          key={it.label}
          {...it}
          kind="yes"
          side="left"
          index={i}
          active={active === it.label}
          onActivate={() => setActive(it.label)}
          binPulse={triggerPulse}
        />
      ))}
    </div>
  );
  const rejectedItems = (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-1">
      {REJECTED_ITEMS.map((it, i) => (
        <StageItem
          key={it.label}
          {...it}
          kind="no"
          side="right"
          index={i}
          active={active === it.label}
          onActivate={() => setActive(it.label)}
          binPulse={triggerPulse}
        />
      ))}
    </div>
  );

  return (
    <div ref={stageRef} className="relative mt-14">
      {/* Soft radial backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-primary/20 via-accent/15 to-transparent blur-3xl"
      />

      {/* MOBILE / TABLET: stacked — Goes in (top), Bin, Keep out (bottom) */}
      <div className="flex flex-col items-stretch gap-6 lg:hidden">
        <div className="space-y-3">
          {AcceptedHeader}
          {acceptedItems}
        </div>
        <div className="flex justify-center">{Bin}</div>
        <div className="space-y-3">
          {RejectedHeader}
          {rejectedItems}
        </div>
      </div>

      {/* DESKTOP: 3-column layout */}
      <div className="hidden grid-cols-[1fr_minmax(260px,360px)_1fr] items-center gap-10 lg:grid">
        <div>
          <div className="mb-5 flex items-center justify-end">{AcceptedHeader}</div>
          {acceptedItems}
        </div>
        {Bin}
        <div>
          <div className="mb-5 flex items-center justify-start">{RejectedHeader}</div>
          {rejectedItems}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------
   Data
   ------------------------------------------------------------------ */
const FINDER_TABS = [
  {
    value: "oakland",
    label: "Oakland Recycles",
    headline: "Oakland Recycles",
    badge: "Oakland · Residential & Property",
    theme: {
      // Brand blue — primary CWS
      panel: "from-[#EAF4FA] via-[#F2F8FB] to-[#FFFFFF]",
      ring: "ring-[#005696]/15",
      accent: "#005696",
      accentDeep: "#003E73",
      chipBg: "bg-white",
      chipText: "text-[#005696]",
      glow: "from-[#005696]/22 via-[#2E7D32]/10 to-transparent",
      btnGradient: "linear-gradient(135deg, #005696 0%, #004A80 60%, #003E73 100%)",
      iconBg: "linear-gradient(135deg, #EAF4FA 0%, #FFFFFF 100%)",
    },
    description:
      "Find support for Oakland residential recycling, multifamily recycling, used motor oil and oil filters, household batteries, recycling carts, missed pickups, customer care, and commercial recycling questions.",
    steps: [
      { icon: Recycle, label: "Residential Recycling", href: "/oakland#residential" },
      { icon: Building2, label: "Commercial Quote", href: "/commercial#quote" },
      { icon: Droplet, label: "Used Motor Oil and Oil Filters", href: "/oakland#special-programs" },
      { icon: Battery, label: "Household Batteries", href: "/oakland#special-programs" },
      { icon: Truck, label: "Report Missed Pickup", href: "/report-a-missed-pickup" },
      { icon: Container, label: "What To Recycle", href: "/recycling-guide" },
    ],
    primary: { label: "Open Oakland Services", href: "/oakland" },
    secondary: { label: "Customer Support", href: "/contact?city=oakland" },
  },
  {
    value: "san-jose",
    label: "San José Recycles",
    headline: "San José Recycles",
    badge: "San José · Recycle Plus",
    theme: {
      // Brand recycling green
      panel: "from-[#EAF5EB] via-[#F2F9F2] to-[#FFFFFF]",
      ring: "ring-[#2E7D32]/15",
      accent: "#2E7D32",
      accentDeep: "#1F5A24",
      chipBg: "bg-white",
      chipText: "text-[#2E7D32]",
      glow: "from-[#2E7D32]/22 via-[#005696]/10 to-transparent",
      btnGradient: "linear-gradient(135deg, #2E7D32 0%, #256B2A 60%, #1F5A24 100%)",
      iconBg: "linear-gradient(135deg, #EAF5EB 0%, #FFFFFF 100%)",
    },
    description:
      "Find support for San José recycling service, service area guidance, used oil and oil filters, large item pickup information, cart support, missed pickup routing, and customer service for eligible CWS served areas.",
    steps: [
      { icon: Recycle, label: "Residential Recycling", href: "/san-jose#residential" },
      { icon: MapPin, label: "Service Area and Schedule Lookup", href: "/san-jose#service-area" },
      { icon: Droplet, label: "Used Oil and Oil Filters", href: "/san-jose#used-oil" },
      { icon: Package, label: "Junk Pickup", href: "/san-jose/junk-pickup" },
      { icon: Truck, label: "Report Missed Pickup", href: "/report-a-missed-pickup" },
      { icon: Container, label: "What To Recycle", href: "/recycling-guide" },
    ],
    primary: { label: "Open San José Services", href: "/san-jose" },
    secondary: { label: "Customer Support", href: "/contact?city=san-jose" },
    note: "Service availability and routing may vary by address.",
  },
  {
    value: "commercial",
    label: "Commercial Recycling",
    headline: "Commercial Recycling Services",
    badge: "Commercial · Property & Business",
    theme: {
      // Deep teal — bridges blue + green for commercial / civic
      panel: "from-[#E8F2F2] via-[#F1F7F6] to-[#FFFFFF]",
      ring: "ring-[#15616D]/15",
      accent: "#15616D",
      accentDeep: "#0E444C",
      chipBg: "bg-white",
      chipText: "text-[#15616D]",
      glow: "from-[#15616D]/22 via-[#2E7D32]/10 to-transparent",
      btnGradient: "linear-gradient(135deg, #15616D 0%, #0E444C 60%, #082F35 100%)",
      iconBg: "linear-gradient(135deg, #E8F2F2 0%, #FFFFFF 100%)",
    },
    description:
      "Get support for businesses, properties, property managers, offices, warehouses, restaurants, retail centers, and multi tenant sites that need recycling service, container planning, account support, or a quote.",
    steps: [
      { icon: Building2, label: "Business Recycling", href: "/commercial" },
      { icon: Headset, label: "Property Manager Support", href: "/commercial" },
      { icon: Layers, label: "Multi Tenant Properties", href: "/commercial" },
      { icon: Container, label: "Commercial Containers", href: "/commercial" },
      { icon: Wrench, label: "Service Support", href: "/contact?service=commercial-support" },
      { icon: Scale, label: "Request a Quote", href: "/commercial#quote" },
    ],
    primary: { label: "View Commercial Services", href: "/commercial" },
    secondary: { label: "Request a Quote", href: "/commercial#quote" },
  },
  {
    value: "general",
    label: "General Support",
    headline: "Contact the Right Team",
    badge: "General · Customer Care",
    theme: {
      // Soft neutral with brand blue accent — keeps it on-brand
      panel: "from-[#F1F5F8] via-[#F6F9FB] to-[#FFFFFF]",
      ring: "ring-[#005696]/12",
      accent: "#005696",
      accentDeep: "#2E7D32",
      chipBg: "bg-white",
      chipText: "text-[#005696]",
      glow: "from-[#005696]/18 via-[#2E7D32]/12 to-transparent",
      btnGradient: "linear-gradient(135deg, #005696 0%, #1A6E73 55%, #2E7D32 100%)",
      iconBg: "linear-gradient(135deg, #EAF4FA 0%, #EAF5EB 100%)",
    },
    description:
      "Use this path for customer support, recycling questions, account help, public inquiries, company information, and general service requests.",
    steps: [
      { icon: Newspaper, label: "Media Inquiry", href: "/contact?service=media" },
      { icon: ShieldCheck, label: "Customer Login", href: "/login" },
      { icon: Scale, label: "Sell / Buy Materials", href: "/contact?service=buy-back" },
      { icon: Headset, label: "Customer Support", href: "/contact" },
    ],
    primary: { label: "Contact CWS", href: "/contact" },
    secondary: { label: "View Recycling Guide", href: "/recycling-guide" },
  },
];

const SERVICES = [
  {
    nav: "Collection and Processing",
    icon: Truck,
    headline: "Collection and Processing",
    description:
      "CWS provides residential, multifamily, commercial, and city program recycling services supported by scheduled routes, trained teams, cart and container service, dispatch coordination, and facility processing.",
    capabilities: [
      "Residential recycling",
      "Multifamily recycling",
      "Commercial recycling",
      "City program recycling",
      "Cart and container service",
      "Route and dispatch coordination",
    ],
    image: svcCollection,
    imageAlt: "California Waste Solutions recycling collection trucks parked in a row at an operations yard",
    cta: { label: "View Recycling Services", href: "/commercial" },
  },
  {
    nav: "Materials Recovery",
    icon: Factory,
    headline: "Materials Recovery Facility (MRF) Operations",
    description:
      "Recyclable materials are received, sorted, inspected, processed, baled, stored, and prepared for market at our Materials Recovery Facilities (MRFs). This is where collected material becomes recovered commodity.",
    capabilities: [
      "Material receiving",
      "Sorting and separation",
      "Quality control",
      "Baling",
      "Bale storage",
      "Market preparation",
    ],
    image: svcMrf,
    imageAlt: "Workers in high visibility jackets sorting recyclables along a conveyor at a California Waste Solutions materials recovery facility, with baled material stacked behind them",
    cta: { label: "View Facility Operations", href: "/sustainability" },
  },
  {
    nav: "Commodity Marketing",
    icon: Package,
    headline: "Recovered Materials Prepared for New Use",
    description:
      "CWS markets recovered recyclable materials to environmentally responsible domestic and global buyers who use those materials in new products, packaging, and manufacturing.",
    capabilities: [
      "Cardboard",
      "Mixed paper",
      "Newspaper",
      "Plastic containers",
      "Metal containers",
      "Commodity preparation",
    ],
    image: svcCommodity,
    imageAlt: "California Waste Solutions worker in a hard hat inspecting stacked bales of recovered cardboard and mixed paper inside a warehouse",
    cta: { label: "Learn About Recovery", href: "/sustainability" },
  },
  {
    nav: "Commercial Recycling",
    icon: Container,
    headline: "Commercial Recycling for Businesses and Properties",
    description:
      "CWS supports businesses, property managers, retail centers, restaurants, offices, warehouses, and multi-tenant sites with practical recycling service, container planning, pickup coordination, and account support.",
    capabilities: [
      "Business recycling",
      "Property manager support",
      "Container planning",
      "Service scheduling",
      "Commercial customer support",
      "Multi-tenant properties",
    ],
    image: svcCommercial,
    imageAlt: "Blue California Waste Solutions commercial recycling dumpster and rolling cart in front of the downtown Oakland skyline",
    cta: { label: "Request a Commercial Quote", href: "/commercial#quote" },
  },
  {
    nav: "Buy-Back and Bulk Materials",
    icon: Scale,
    headline: "Buy-Back and Bulk Recyclable Materials",
    description:
      "CWS accepts bulk recyclable materials from businesses and community partners, including cardboard, newspaper, mixed paper, and other approved recyclable commodities.",
    capabilities: [
      "Cardboard",
      "Newspaper",
      "Mixed paper",
      "Bulk recyclable materials",
      "Commodity handling",
      "Drop-off and buy-back support",
    ],
    image: svcBuyback,
    imageAlt: "Workers loading flattened cardboard, newspaper and aluminum cans into blue drop-off bins with a California Waste Solutions collection truck behind them",
    cta: { label: "Contact Buy-Back Support", href: "/contact?service=buy-back" },
  },
  {
    nav: "Waste Infrastructure",
    icon: Building2,
    headline: "Large-Scale Waste Infrastructure",
    description:
      "Through Vietnam Waste Solutions, CWS extends its environmental work into integrated waste management, material recovery, transfer operations, composting, leachate treatment, landfill gas recovery, and long-term infrastructure development.",
    capabilities: [
      "Integrated waste management",
      "Material recovery",
      "Transfer operations",
      "Composting",
      "Leachate treatment",
      "Landfill gas recovery",
    ],
    image: svcInfrastructure,
    imageAlt: "Aerial view of the Vietnam Waste Solutions facility showing sorting halls, treatment ponds and a lined landfill area",
    cta: { label: "Explore Global Operations", href: "/about#vietnam" },
  },
];

const PROCESS_STEPS = [
  { icon: Container, title: "Set Out", text: "Place clean recyclables in your curbside cart." },
  { icon: Truck, title: "Collection", text: "Drivers collect carts along service routes." },
  { icon: Scale, title: "Receiving", text: "Loads arrive at the MRF and are weighed." },
  { icon: ScanLine, title: "Sorting", text: "Scanners and crews separate each material." },
  { icon: Package, title: "Baling", text: "Sorted material is pressed into bales." },
  { icon: Boxes, title: "Market Prep", text: "Bales ship to mills to make new products." },
];

const COMMERCIAL_CUSTOMERS = [
  "Office buildings",
  "Retail centers",
  "Restaurants",
  "Warehouses",
  "Industrial sites",
  "Property managers",
  "Multifamily properties",
  "Mixed use properties",
];

const GUIDE_CARDS = [
  {
    title: "Recycle",
    icon: Recycle,
    soft: "bg-[hsl(var(--primary-soft))]",
    iconColor: "text-primary",
    text: "Empty bottles and cans, clean paper, flattened cardboard, and rigid plastic containers. Keep items loose, empty, and dry.",
  },
  {
    title: "Compost",
    icon: Leaf,
    soft: "bg-[hsl(var(--accent-soft))]",
    iconColor: "text-accent",
    text: "Food scraps, food soiled paper, plant debris, and untreated wood where compost service is provided by the local program.",
  },
  {
    title: "Trash",
    icon: Trash2,
    soft: "bg-[hsl(0_0%_95%)]",
    iconColor: "text-charcoal",
    text: "Diapers, pet waste, polystyrene, film plastic, food wrappers, and items that are not recyclable or compostable.",
  },
  {
    title: "Special Handling",
    icon: Battery,
    soft: "bg-[hsl(28_92%_95%)]",
    iconColor: "text-[hsl(28_88%_45%)]",
    text: "Used motor oil, oil filters, household batteries, electronics, paint, chemicals, fluorescent bulbs, and sharps must be handled separately.",
  },
];

const PILLARS = [
  { icon: Recycle, title: "Recover More Material", text: "Collect, sort, and process recyclable materials so they can return to productive use." },
  { icon: ShieldCheck, title: "Reduce Contamination", text: "Help customers keep food, liquids, plastic bags, and hazardous items out of recycling." },
  { icon: Factory, title: "Operate Responsibly", text: "Support safe work, careful handling, environmental controls, and reliable service." },
  { icon: GraduationCap, title: "Educate the Community", text: "Give residents, property managers, and businesses clear guidance they can follow." },
];

const FACILITY_FEATURES = [
  { icon: Factory, title: "Materials Recovery Facility" },
  { icon: Scale, title: "Material Receiving" },
  { icon: ScanLine, title: "Sorting and Processing" },
  { icon: Package, title: "Bale Storage" },
  { icon: Wrench, title: "Truck Maintenance" },
  { icon: ShieldCheck, title: "Quality Control" },
  { icon: Leaf, title: "Environmental Controls" },
  { icon: Headset, title: "Customer Support Systems" },
];

const VWS_HIGHLIGHTS = [
  { icon: Factory, title: "Da Phuoc Integrated Facility", text: "Large scale waste management and environmental infrastructure." },
  { icon: Boxes, title: "Material Recovery and Transfer", text: "Receiving, processing, transfer, and recovery operations." },
  { icon: Droplet, title: "Leachate Treatment", text: "Environmental treatment systems designed for safe facility operation." },
  { icon: Flame, title: "Landfill Gas Recovery", text: "Systems that capture landfill gas and support better environmental controls." },
  { icon: Leaf, title: "Long An Green Environmental Technology Park", text: "Long horizon infrastructure investment for regional environmental needs." },
];

const NEWS_CARDS: Array<{
  tag: string;
  title: string;
  text: string;
  href: string;
  video?: string;
  image?: string;
  alt?: string;
}> = [
  { tag: "Company News", title: "Updates from California Waste Solutions", text: "Community programs, milestones, and company activity.", href: "/news#company", video: newsCompanyVideo },
  { tag: "Service Updates", title: "Holiday Schedules and Route Notices", text: "Customer alerts and service reminders that affect your pickup day.", href: "/news#service", image: newsServiceUpdates, alt: "California Waste Solutions collection truck on route" },
  { tag: "Public Notices", title: "Facility and Public Information", text: "Public meeting information, environmental updates, and public information.", href: "/news#press", image: newsPublicNotices, alt: "California Waste Solutions recycling and trash carts with signage" },
];

/* ------------------------------------------------------------------
   Page
   ------------------------------------------------------------------ */
const Index = () => {
  const revealRef = useReveal<HTMLDivElement>();
  const [activeService, setActiveService] = useState(0);
  const [activeFinderTab, setActiveFinderTab] = useState<string>(FINDER_TABS[0].value);

  return (
    <Layout>
      <Seo
        title="California Waste Solutions (Cal Waste) | Bay Area Recycling"
        description="California Waste Solutions (Cal Waste) provides recycling pickup, customer service and billing help for Oakland and San José homes and businesses."
        keywords={["California Waste Solutions","CWS","CWS recycling","recycling near me","recycling services","recycle center near me","waste management services","waste management near me","recycling pickup","resource recovery company","environmental services company","municipal recycling partner","public sector recycling services","materials recovery facility","commercial recycling California","sustainable waste management","circular economy recycling","environmental infrastructure company","zero waste infrastructure","California recycling company","Bay Area recycling company","Northern California recycling company","Oakland recycling","San Jose recycling","Vietnam Waste Solutions","VWS","David Duong","Duong family"]}
        jsonLd={[
          orgJsonLd(),
          websiteJsonLd(),
          breadcrumbJsonLd([{ name: "Home", path: "/" }]),
          localBusinessJsonLd("Oakland"),
          localBusinessJsonLd("San Jose"),
          itemListJsonLd([
            { name: "Oakland Recycling Services", path: "/oakland", description: "Curbside, multifamily, and commercial recycling for the City of Oakland." },
            { name: "San José Recycling Services", path: "/san-jose", description: "Curbside recycling and resource recovery for the City of San José." },
            { name: "Commercial Recycling", path: "/commercial", description: "Business and property recycling programs across California." },
            { name: "Recycling Guide", path: "/recycling-guide", description: "What goes where: a complete sorting guide." },
          ]),
          serviceJsonLd({
            name: "Municipal Curbside Recycling",
            description: "Curbside collection, processing, and resource recovery for municipal partners across California.",
            path: "/oakland",
            serviceType: "Municipal Recycling",
          }),
          serviceJsonLd({
            name: "Materials Recovery Facility (MRF) Operations",
            description: "Large-scale MRF processing, sorting, and commodity recovery for public agencies and commercial generators.",
            path: "/sustainability",
            serviceType: "Materials Recovery Facility",
          }),
          serviceJsonLd({
            name: "Commercial & Multifamily Recycling",
            description: "Recycling programs, container service, and contamination reduction for businesses and properties.",
            path: "/commercial",
            serviceType: "Commercial Recycling",
          }),
        ]}
      />
      <div ref={revealRef}>
        {/* ============ 1. HERO ============ */}
        <section className="relative isolate overflow-hidden text-white">
          {/* Background: video only */}
          <div className="absolute inset-0 -z-10">
            <VideoHeroBackground
              src={heroVideo}
              poster={heroPoster}
              posterAlt="Two City of San Jose carts with blue recycling lids at the curb as an automated side loader recycling truck extends its lifting arms on a residential street"
              priority
              videoClassName="animate-[heroZoom_24s_ease-out_forwards]"
            />

            {/* Layered overlays — lightened so video reads through */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(115deg, hsl(var(--primary-deep) / 0.55) 0%, hsl(var(--primary-deep) / 0.30) 40%, hsl(var(--primary) / 0.15) 70%, hsl(var(--accent) / 0.10) 100%)",
              }}
            />
            {/* Bottom-up scrim only — keeps top of video clear, protects CTA legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/75 via-charcoal/15 to-transparent" />

            {/* Glow accents — softened */}
            <div
              className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full opacity-20 blur-3xl animate-[heroGlow_9s_ease-in-out_infinite]"
              style={{ background: "radial-gradient(closest-side, hsl(var(--primary) / 0.45), transparent 70%)" }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-40 right-[-10%] h-[560px] w-[560px] rounded-full opacity-20 blur-3xl animate-[heroGlow_11s_ease-in-out_infinite_1s]"
              style={{ background: "radial-gradient(closest-side, hsl(var(--accent) / 0.45), transparent 70%)" }}
              aria-hidden
            />

            {/* Soft grid texture */}
            <div className="absolute inset-0 opacity-[0.05] bg-grid-soft mix-blend-overlay" aria-hidden />

            {/* Vignette — lighter */}
            <div
              className="absolute inset-0"
              style={{ background: "radial-gradient(120% 80% at 50% 40%, transparent 60%, hsl(0 0% 0% / 0.35) 100%)" }}
              aria-hidden
            />
          </div>

          <div className="container-cws relative pt-20 pb-56 sm:pt-28 sm:pb-52 md:pt-32 md:pb-48 lg:pt-36 lg:pb-56">
            <div className="max-w-5xl">
              {/* Eyebrow chip — hidden on desktop, shown on tablet/mobile */}
              <span className="reveal lg:hidden inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[9.5px] sm:text-[11px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-md shadow-[0_2px_20px_-6px_hsl(var(--primary)/0.6)]">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                </span>
                Serving Communities Since 1992
              </span>

              {/* Headline with gradient highlight */}
              <h1
                className="reveal mt-4 sm:mt-6 font-extrabold text-balance text-white"
                style={{
                  fontSize: "clamp(1.875rem, 1.1rem + 4vw, 5rem)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.025em",
                }}
              >
                {/* One headline, single DOM tree. Inline on mobile, two lines from sm up. */}
                <span className="sm:block">Building The Infrastructure</span>{" "}
                <span className="sm:block">
                  For A{" "}
                  <span
                    className="bg-clip-text text-transparent animate-[heroSheen_8s_ease-in-out_infinite]"
                    style={{
                      backgroundImage:
                        "linear-gradient(100deg, hsl(var(--accent)) 0%, hsl(122 55% 55%) 25%, hsl(140 65% 70%) 50%, hsl(122 55% 55%) 75%, hsl(var(--accent)) 100%)",
                      backgroundSize: "200% 100%",
                      filter: "drop-shadow(0 2px 18px hsl(var(--accent) / 0.55))",
                    }}
                  >
                    Zero Waste Future
                  </span>
                </span>
              </h1>

              {/* Subheadline — shorter on mobile, full copy on tablet+ */}
              <p className="reveal mt-5 sm:mt-7 text-base sm:text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
                <span className="sm:hidden">
                  Serving Oakland and San José with reliable service and resource recovery built to last.
                </span>
                <span className="hidden sm:inline">
                  Serving Oakland and San José with disciplined operations, responsive customer support, and resource
                  recovery systems built for long term public performance.
                </span>
              </p>

              {/* CTAs */}
              <div className="reveal mt-9 flex flex-wrap gap-3">
                <Button asChild variant="cta" size="lg" className="group h-14 px-7 text-[15px] shadow-[0_14px_40px_-12px_hsl(var(--primary)/0.8)]" onClick={() => trackPublicCta("Get Support (Hero)", { target_path: "/contact" })}>
                  <Link to="/contact" id="cta-home-hero-get-support" data-gtm="cta_click" data-gtm-id="get_support" data-gtm-location="home_hero" data-gtm-type="cta">
                    Get Support
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outlineLight"
                  size="lg"
                  className="group h-14 px-7 text-[15px] backdrop-blur-md bg-white/5 border-white/30 hover:bg-white hover:text-accent"
                  onClick={() => trackPublicCta("Explore Services (Hero)", { target_path: "/commercial" })}
                >
                  <Link to="/commercial" id="cta-home-hero-explore-services" data-gtm="cta_click" data-gtm-id="explore_services" data-gtm-location="home_hero" data-gtm-type="cta">
                    Explore Services
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes heroZoom {
              from { transform: scale(1); }
              to   { transform: scale(1.08); }
            }
            @keyframes heroGlow {
              0%, 100% { transform: translate3d(0,0,0) scale(1); opacity: 0.4; }
              50%      { transform: translate3d(20px,-10px,0) scale(1.08); opacity: 0.6; }
            }
            @keyframes heroSheen {
              0%, 100% { background-position: 0% 50%; }
              50%      { background-position: 100% 50%; }
            }
          `}</style>
        </section>

        {/* ============ Quick Access seam bar (between hero and next section) ============ */}
        <div className="relative z-30 -mt-10 sm:-mt-16 md:-mt-24 lg:-mt-28">
          <div className="container-cws">
            <div
              className="rounded-2xl border border-border/70 p-3 md:p-3.5 shadow-[0_30px_80px_-30px_hsl(var(--primary-deep)/0.55)] backdrop-blur-sm"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, hsl(0 0% 100% / 0.95) 0%, hsl(var(--soft-bg)) 100%)",
              }}
            >
              {/* Top row: header + all paths */}
              <div className="flex items-center justify-between px-1.5 pt-1 pb-2.5">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--accent-soft))] text-accent shadow-sm">
                    <Recycle className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-[11px] md:text-[11.5px] font-bold uppercase tracking-[0.18em] text-charcoal/80">
                    Quick Access
                  </span>
                </div>

                {/* Swipe hint — only visible while the row is scrollable (below lg) */}
                <div className="flex lg:hidden items-center gap-1.5 rounded-full border border-border/70 bg-white/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-charcoal/70 shadow-sm">
                  <ChevronLeft className="h-3 w-3 animate-[swipe-hint_1.6s_ease-in-out_infinite_reverse] text-accent" aria-hidden />
                  <span>Swipe</span>
                  <ChevronRight className="h-3 w-3 animate-[swipe-hint_1.6s_ease-in-out_infinite] text-accent" aria-hidden />
                </div>
              </div>

              {/* Items row: horizontal scroll on small, equal grid on lg+ */}
              <div className="flex items-stretch gap-2 overflow-x-auto lg:grid lg:grid-cols-5 lg:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[
                  { icon: Truck, label: "Missed Pick up", href: "/report-a-missed-pickup" },
                  { icon: Container, label: "Cart & Container", href: "/contact?service=cart-container" },
                  { icon: Package, label: "Bulky Item Pick up", href: "/contact#san-jose" },
                  { icon: Building2, label: "Commercial Quote", href: "/commercial#quote" },
                  { icon: Recycle, label: "Recycling Guidelines", href: "/recycling-guide" },
                ].map((s) => (
                  <Link
                    key={s.label}
                    to={s.href}
                    className="group relative flex shrink-0 min-w-[200px] lg:min-w-0 items-center justify-between gap-2.5 rounded-xl border border-transparent bg-white/80 px-3.5 py-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-white hover:shadow-[0_12px_30px_-12px_hsl(var(--accent)/0.45)]"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--primary-soft))] text-primary transition-all duration-300 group-hover:bg-accent group-hover:text-white group-hover:scale-110 group-hover:shadow-[0_6px_18px_-6px_hsl(var(--accent)/0.7)]">
                        <s.icon className="h-4 w-4" />
                      </span>
                      <span className="truncate text-[13px] font-semibold text-charcoal">
                        {s.label}
                      </span>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-1 group-hover:text-accent" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ============ 2. SERVICE FINDER ============ */}
        <section className="relative -mt-20 md:-mt-24 lg:-mt-28 pt-32 md:pt-36 lg:pt-40 pb-20 md:pb-24 lg:pb-28 border-b border-border overflow-hidden bg-gradient-to-b from-[hsl(var(--soft-bg))] via-white to-[#F4F9F4]">
          {/* Organic eco background — soft leaf blobs + dotted recycle motif */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-[42%_58%_63%_37%/45%_42%_58%_55%] bg-gradient-to-br from-primary/12 via-primary/5 to-transparent blur-3xl" />
            <div className="absolute -bottom-32 -right-24 h-[30rem] w-[30rem] rounded-[58%_42%_38%_62%/55%_60%_40%_45%] bg-gradient-to-tl from-accent/14 via-accent/6 to-transparent blur-3xl" />
          </div>

          <div className="container-cws relative">
            {/* Header — matches site eyebrow + display style */}
            <div className="mx-auto max-w-3xl text-center reveal">
              <span className="eyebrow-row justify-center">
                Service Finder
              </span>
              <h2 className="display-2 mt-4 text-charcoal">
                How Can We <span className="gradient-text-blue-green">Help You</span> Today?
              </h2>
              <p className="lead mt-4 text-muted-foreground max-w-2xl mx-auto text-balance">
                Choose your city or service need, and we will guide you to the right support&nbsp;path.
              </p>
            </div>

            <div className="mt-12 md:mt-14 reveal">
              <Tabs value={activeFinderTab} onValueChange={setActiveFinderTab} className="w-full">
                {/* Softer, eco-styled tab bar */}
                <div className="flex flex-col items-center gap-2">
                  <div className="overflow-x-auto max-w-full -mx-4 px-4 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <TabsList className="inline-flex h-auto w-auto justify-start gap-1 rounded-full border border-border/60 bg-white/85 backdrop-blur-md p-1.5 shadow-[0_18px_40px_-22px_hsl(var(--primary)/0.35)]">
                      {FINDER_TABS.map((t) => (
                        <TabsTrigger
                          key={t.value}
                          value={t.value}
                          onMouseEnter={() => setActiveFinderTab(t.value)}
                          onFocus={() => setActiveFinderTab(t.value)}
                          style={{ ['--tab-grad' as any]: t.theme.btnGradient }}
                          className="relative whitespace-nowrap rounded-full px-4 md:px-5 py-2.5 text-[13px] md:text-[13.5px] font-semibold text-muted-foreground transition-all duration-300
                                     data-[state=active]:text-white data-[state=active]:shadow-[0_12px_28px_-12px_rgba(0,0,0,0.45)]
                                     data-[state=active]:[background-image:var(--tab-grad)]
                                     hover:text-charcoal"
                        >
                          {t.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {/* Mobile swipe hint for tabs */}
                  <div className="flex md:hidden items-center gap-1.5 rounded-full border border-border/70 bg-white/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-charcoal/70 shadow-sm">
                    <ChevronLeft className="h-3 w-3 animate-[swipe-hint_1.6s_ease-in-out_infinite_reverse] text-accent" aria-hidden />
                    <span>Swipe</span>
                    <ChevronRight className="h-3 w-3 animate-[swipe-hint_1.6s_ease-in-out_infinite] text-accent" aria-hidden />
                  </div>
                </div>

                {FINDER_TABS.map((t) => (
                  <TabsContent
                    key={t.value}
                    value={t.value}
                    className="mt-8 md:mt-10 animate-fade-in"
                  >
                    <div
                      className={`relative overflow-hidden rounded-[28px] border border-white/80 bg-gradient-to-br ${t.theme.panel} p-6 md:p-10 lg:p-12 shadow-[0_40px_90px_-50px_rgba(15,42,32,0.5)] ring-1 ${t.theme.ring}`}
                    >
                      {/* Organic glow blobs themed per tab */}
                      <div
                        aria-hidden
                        className={`pointer-events-none absolute -top-28 -right-20 h-80 w-80 rounded-[42%_58%_63%_37%/45%_42%_58%_55%] bg-gradient-to-br ${t.theme.glow} blur-3xl`}
                      />
                      <div
                        aria-hidden
                        className={`pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-[58%_42%_38%_62%/55%_60%_40%_45%] bg-gradient-to-tr ${t.theme.glow} blur-3xl opacity-70`}
                      />
                      {/* (top hairline removed per design) */}

                      <div className="relative grid gap-10 lg:grid-cols-12 lg:gap-12">
                        <div className="lg:col-span-5">
                          {/* Themed location badge */}
                          <span
                            className={`inline-flex items-center gap-2 rounded-full ${t.theme.chipBg} ${t.theme.chipText} px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] ring-1`}
                            style={{ boxShadow: `0 8px 22px -12px ${t.theme.accent}66`, ['--tw-ring-color' as any]: `${t.theme.accent}33` }}
                          >
                            <MapPin className="h-3.5 w-3.5" />
                            {t.badge ?? t.label}
                          </span>

                          <h3 className="mt-5 text-3xl md:text-[2.4rem] font-extrabold tracking-tight text-charcoal leading-[1.1]">
                            {t.headline}
                          </h3>

                          {/* Themed underline accent */}
                          <div
                            aria-hidden
                            className="mt-4 h-1 w-14 rounded-full"
                            style={{ backgroundImage: t.theme.btnGradient }}
                          />

                          <p className="mt-5 text-[15px] leading-relaxed text-muted-foreground">
                            {t.description}
                          </p>
                          {t.note && (
                            <p className="mt-3 text-[12.5px] italic text-muted-foreground">{t.note}</p>
                          )}

                          <div className="mt-7 flex flex-wrap gap-3">
                            <Button
                              asChild
                              variant="cta"
                              size="lg"
                              className="group h-14 px-7 text-[15px] shadow-[0_14px_40px_-12px_hsl(var(--primary)/0.8)]"
                            >
                              <Link to={t.primary.href}>
                                {t.primary.label}
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                              </Link>
                            </Button>
                            <Button
                              asChild
                              variant="outline"
                              size="lg"
                              className="group h-14 px-7 text-[15px] bg-white/90 backdrop-blur border-border text-charcoal hover:border-accent hover:text-accent hover:bg-white hover:-translate-y-0.5"
                            >
                              <Link to={t.secondary.href}>
                                {t.secondary.label}
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                              </Link>
                            </Button>
                          </div>
                        </div>

                        <div className="lg:col-span-7">
                          <button
                            type="button"
                            onClick={(e) => {
                              const panel = e.currentTarget.nextElementSibling as HTMLElement | null;
                              const icon = e.currentTarget.querySelector('[data-qa-chev]') as HTMLElement | null;
                              panel?.classList.toggle('hidden');
                              icon?.classList.toggle('rotate-180');
                            }}
                            className="flex w-full items-center justify-between gap-3 rounded-xl border border-border/60 bg-white/80 px-4 py-3 shadow-sm md:cursor-default md:border-0 md:bg-transparent md:p-0 md:shadow-none md:pointer-events-none"
                            aria-label="Toggle Quick Access"
                          >
                            <div className="flex items-center gap-3">
                              <span
                                aria-hidden
                                className="hidden md:block h-px w-8 rounded-full"
                                style={{ backgroundColor: `${t.theme.accent}66` }}
                              />
                              <p
                                className="text-[11px] font-bold uppercase tracking-[0.22em]"
                                style={{ color: t.theme.accent }}
                              >
                                QUICK ACCESS
                              </p>
                              <span className="md:hidden text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                · {t.steps.length} options
                              </span>
                            </div>
                            <ChevronDown
                              data-qa-chev
                              className="h-4 w-4 text-muted-foreground transition-transform duration-300 md:hidden"
                              aria-hidden
                            />
                          </button>

                          <div className="mt-5 hidden md:grid gap-2 sm:grid-cols-2 animate-fade-in">
                            {t.steps.map((s) => (
                              <Link
                                key={s.label}
                                to={s.href}
                                className="group relative flex items-center justify-between gap-2.5 rounded-xl border border-transparent bg-white/90 backdrop-blur px-3.5 py-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-white hover:shadow-[0_12px_30px_-12px_hsl(var(--accent)/0.45)]"
                              >
                                <div className="flex min-w-0 items-center gap-2.5">
                                  <span className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--primary-soft))] text-primary transition-all duration-300 group-hover:bg-accent group-hover:text-white group-hover:scale-110 group-hover:shadow-[0_6px_18px_-6px_hsl(var(--accent)/0.7)]">
                                    <s.icon className="h-4 w-4" />
                                  </span>
                                  <span className="truncate text-[13px] font-semibold text-charcoal">
                                    {s.label}
                                  </span>
                                </div>
                                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-all duration-300 group-hover:translate-x-1 group-hover:text-accent" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </div>
        </section>

        {/* ============ 3. ABOUT CWS — Cinematic company-story moment ============ */}
        <section
          className="relative isolate overflow-hidden"
          aria-label="About California Waste Solutions"
        >
          {/* Background: operations video only */}
          <div className="absolute inset-0 -z-10">
            <VideoHeroBackground
              src={aboutCwsOperationsVideo}
              poster={aboutCwsOperationsPoster}
              posterAlt="California Waste Solutions crew in yellow hard hats and high visibility jackets hand sorting paper, plastic film and containers along a conveyor inside a materials recovery facility"
            />
            {/* Refined editorial overlay: deep navy fading to clear on the right for depth */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(95deg, hsl(210 100% 8% / 0.88) 0%, hsl(210 100% 12% / 0.78) 38%, hsl(210 100% 14% / 0.45) 70%, hsl(122 46% 18% / 0.35) 100%)",
              }}
              aria-hidden
            />
            {/* Top + bottom vignette for cinematic framing */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, hsl(0 0% 0% / 0.35) 0%, transparent 18%, transparent 82%, hsl(0 0% 0% / 0.45) 100%)",
              }}
              aria-hidden
            />
            {/* Faint film grid for texture */}
            <div className="absolute inset-0 bg-grid-faint opacity-30" aria-hidden />
          </div>

          <div className="container-cws py-20 md:py-28 lg:py-32">
            <div className="grid lg:grid-cols-12 lg:gap-10">
              <div className="lg:col-span-7 xl:col-span-6 reveal">
                <div className="relative rounded-2xl border border-white/15 bg-[hsl(210_100%_8%/0.5)] p-7 backdrop-blur-md sm:p-9 md:p-10 lg:p-11">
                  <div className="flex items-center gap-3">
                    <span
                      className="h-[2px] w-10 rounded-full"
                      style={{ background: "linear-gradient(90deg, hsl(var(--primary-foreground)), hsl(var(--accent)))" }}
                      aria-hidden
                    />
                    <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/85">
                      About us
                    </span>
                  </div>

                  <h2 className="display-2 mt-4 text-white text-balance">
                    Built to Keep <span className="gradient-text-accent">Recycling Moving</span>
                  </h2>

                  <div className="mt-5 space-y-4 max-w-xl">
                    <p className="text-[15.5px] leading-relaxed text-white/90 md:text-[16px]">
                      California Waste Solutions, also known as Cal Waste, provides essential recycling
                      services for Oakland and the Bay Area. Since 1992, we have worked with cities,
                      businesses, property teams, and residents to support reliable collection, customer
                      service, and material recovery.
                    </p>
                    <p className="text-[14.5px] leading-relaxed md:text-[15px] text-white">
                      From curbside service to MRF processing, our work helps move recyclable materials
                      through the system with greater consistency, cleaner streams, and stronger
                      operational support.
                    </p>
                  </div>

                  <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Button asChild variant="cta" size="lg" className="group w-full sm:w-auto">
                      <Link to="/about">
                        Learn More About CWS
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                    <Button asChild variant="outlineLight" size="lg" className="w-full sm:w-auto">
                      <Link to="/sustainability">View Sustainability</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 4. WHAT WE DO ============ */}
        <section className="bg-[hsl(var(--soft-bg))] py-20 md:py-24 lg:py-28 relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0 surface-radial opacity-60" />
          <div className="container-cws relative">
            <div className="max-w-3xl reveal">
              <Eyebrow>What We Do</Eyebrow>
              <SectionTitle>
                Integrated Recycling, Recovery, and Waste Infrastructure Services
              </SectionTitle>
              <SectionLead className="!max-w-3xl text-pretty">
                California Waste Solutions delivers recycling and resource recovery across the full
                material lifecycle from collection and processing to commodity marketing, commercial
                service, buy-back programs, and large-scale environmental infrastructure.
              </SectionLead>
            </div>

            {/* ====== Showcase: nav + active panel ====== */}
            <div className="mt-12 md:mt-14 grid grid-cols-1 sm:grid-cols-12 gap-5 sm:gap-6 lg:gap-8 items-start">
              {/* MOBILE NAV: horizontal scroll tab strip with swipe hint + dots */}
              <div className="sm:hidden reveal">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                    <ChevronLeft className="h-3 w-3" />
                    Swipe
                    <ChevronRight className="h-3 w-3" />
                  </span>
                  <span className="text-[10px] font-bold tracking-[0.18em] tabular-nums text-muted-foreground">
                    0{activeService + 1} / 0{SERVICES.length}
                  </span>
                </div>
                <nav aria-label="CWS services" className="-mx-4 px-4">
                  <ul className="relative flex gap-2.5 overflow-x-auto snap-x snap-mandatory pb-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    {SERVICES.map((s, i) => {
                      const isActive = i === activeService;
                      return (
                        <li key={s.nav} className="shrink-0 snap-start">
                          <button
                            type="button"
                            onClick={() => setActiveService(i)}
                            aria-pressed={isActive}
                            className={`relative flex flex-col items-center gap-1.5 rounded-xl border px-3 py-2.5 w-[112px] text-center transition-all duration-300 ${
                              isActive
                                ? "border-primary bg-white shadow-md"
                                : "border-border bg-white/70"
                            }`}
                          >
                            <span
                              className={`text-[9.5px] font-bold tracking-[0.2em] tabular-nums ${
                                isActive ? "text-primary" : "text-muted-foreground/70"
                              }`}
                            >
                              0{i + 1}
                            </span>
                            <span
                              className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition-all duration-300 ${
                                isActive
                                  ? "bg-gradient-cta text-white shadow-[0_8px_22px_-8px_hsl(var(--primary)/0.6)]"
                                  : "bg-[hsl(var(--primary-soft))] text-primary"
                              }`}
                            >
                              <s.icon className="h-4 w-4" />
                            </span>
                            <span
                              className={`text-[10.5px] font-bold leading-tight line-clamp-2 ${
                                isActive ? "text-charcoal" : "text-charcoal/70"
                              }`}
                            >
                              {s.nav}
                            </span>
                            {isActive && (
                              <span aria-hidden className="absolute -bottom-[1px] left-3 right-3 h-0.5 rounded-full bg-gradient-to-r from-primary to-accent" />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
                {/* Dot indicators */}
                <div className="flex items-center justify-center gap-1.5 mt-1">
                  {SERVICES.map((s, i) => (
                    <button
                      key={s.nav}
                      type="button"
                      aria-label={`Go to ${s.nav}`}
                      onClick={() => setActiveService(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === activeService ? "w-6 bg-primary" : "w-1.5 bg-border"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* DESKTOP/TABLET NAV: vertical list */}
              <nav aria-label="CWS services" className="hidden sm:block sm:col-span-4 reveal">
                <ul className="relative space-y-1.5 border-l border-border">
                  {SERVICES.map((s, i) => {
                    const isActive = i === activeService;
                    return (
                      <li key={s.nav}>
                        <button
                          type="button"
                          onMouseEnter={() => setActiveService(i)}
                          onFocus={() => setActiveService(i)}
                          onClick={() => setActiveService(i)}
                          aria-pressed={isActive}
                          className={`group relative -ml-px flex w-full items-center gap-3 lg:gap-4 border-l-2 py-3.5 lg:py-4 pl-4 lg:pl-5 pr-3 text-left transition-all duration-300 ${
                            isActive
                              ? "border-primary bg-white shadow-sm"
                              : "border-transparent hover:border-accent/40 hover:bg-white/60"
                          }`}
                        >
                          <span
                            className={`text-[11px] font-bold tracking-[0.18em] tabular-nums transition-colors ${
                              isActive ? "text-primary" : "text-muted-foreground/70"
                            }`}
                          >
                            0{i + 1}
                          </span>
                          <span
                            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-all duration-300 ${
                              isActive
                                ? "bg-gradient-cta text-white scale-105 shadow-[0_8px_22px_-8px_hsl(var(--primary)/0.6)]"
                                : "bg-[hsl(var(--primary-soft))] text-primary group-hover:scale-105"
                            }`}
                          >
                            <s.icon className="h-4.5 w-4.5" />
                          </span>
                          <span
                            className={`flex-1 min-w-0 text-[13.5px] lg:text-[14.5px] font-bold leading-snug transition-colors ${
                              isActive ? "text-charcoal" : "text-charcoal/75 group-hover:text-charcoal"
                            }`}
                          >
                            {s.nav}
                          </span>
                          <ArrowRight
                            className={`h-4 w-4 shrink-0 transition-all duration-300 ${
                              isActive
                                ? "text-primary translate-x-0.5"
                                : "text-muted-foreground/40 group-hover:translate-x-0.5 group-hover:text-accent"
                            }`}
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              {/* RIGHT: active service panel */}
              <div className="sm:col-span-8 reveal">
                {SERVICES.map((s, i) => {
                  const isActive = i === activeService;
                  return (
                    <article
                      key={s.nav}
                      hidden={!isActive}
                      className={`overflow-hidden rounded-2xl border border-border bg-white shadow-card ${
                        isActive ? "animate-fade-in" : ""
                      }`}
                    >
                      <div className="group relative h-[220px] sm:h-[300px] lg:h-[340px] overflow-hidden">
                        <img decoding="async"
                          src={s.image}
                          alt={s.imageAlt}
                          loading="lazy"
                          width={1280}
                          height={896}
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[6000ms] ease-out group-hover:scale-110"
                        />
                        <div
                          aria-hidden
                          className="absolute inset-0"
                          style={{
                            background:
                              "linear-gradient(180deg, hsl(210 100% 12% / 0.05) 0%, hsl(210 100% 10% / 0.45) 70%, hsl(210 100% 8% / 0.85) 100%)",
                          }}
                        />
                        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 lg:p-7">
                          <h3
                            className="text-white font-extrabold leading-tight text-balance"
                            style={{
                              fontSize: "clamp(1.15rem, 0.9rem + 1.2vw, 1.85rem)",
                              letterSpacing: "-0.012em",
                            }}
                          >
                            {s.headline}
                          </h3>
                        </div>
                      </div>

                      <div className="p-5 sm:p-6 lg:p-7">
                        <p className="text-[14px] sm:text-[14.5px] leading-relaxed text-muted-foreground">
                          {s.description}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

          </div>
        </section>

        {/* ============ 4. HOW RECYCLING MOVES ============ */}
        <section className="relative overflow-hidden border-y border-border py-20 md:py-28">
          {/* Video background (mirrors About section treatment) */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <VideoHeroBackground
              src="/__l5e/assets-v1/7823da89-2c53-44a3-b5cd-af18aeafb504/how-recycling-moves.mp4"
              poster="/videos/how-recycling-moves-poster.webp"
              posterAlt="City of San Jose recycling carts at the curb beside an automated side loader recycling truck on a residential street"
            />
            {/* Solid blue editorial overlay */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(95deg, hsl(210 100% 10% / 0.94) 0%, hsl(206 100% 22% / 0.88) 50%, hsl(210 100% 14% / 0.86) 100%)",
              }}
            />
            {/* Soft white glow for depth */}
            <div className="absolute -top-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-white/5 blur-3xl" />
          </div>

          <div className="container-cws relative">
            <div className="max-w-3xl reveal">
              <Eyebrow light>How Recycling Moves</Eyebrow>
              <h2 className="display-2 mt-3 text-white">From Cart to <span className="gradient-text-accent">Recovery</span></h2>
              <p className="lead mt-4 text-white/80">
                A recyclable item moves through several steps before it can become useful again.
              </p>
            </div>

            {/* Desktop horizontal timeline */}
            <div className="mt-14 hidden lg:block">
              <div className="relative">
                {/* Animated dashed line */}
                <div
                  aria-hidden
                  className="absolute left-8 right-8 top-7 h-[2px] bg-[length:14px_2px] bg-repeat-x opacity-50"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, hsl(0 0% 100%) 50%, transparent 50%)",
                  }}
                />
                {/* Glowing flowing pulse */}
                <div
                  aria-hidden
                  className="absolute left-8 right-8 top-[26px] h-1 overflow-hidden rounded-full"
                >
                  <div className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-sm animate-[flow_4s_linear_infinite]" />
                </div>

                <ol className="relative grid grid-cols-6 gap-5">
                  {PROCESS_STEPS.map((s, i) => (
                    <li
                      key={s.title}
                      className="reveal group flex flex-col"
                      style={{ transitionDelay: `${i * 90}ms` }}
                    >
                      <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white border-2 border-white text-primary shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:bg-accent group-hover:border-accent group-hover:text-white group-hover:shadow-glow-green">
                        <span aria-hidden className="absolute inset-0 rounded-full border-2 border-accent/70 opacity-0 group-hover:opacity-100 group-hover:animate-ping" />
                        <s.icon className="h-5 w-5 transition-transform duration-500 group-hover:scale-110" />
                        <span className="absolute -top-2 -right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-white shadow-md ring-2 ring-white/30 transition-all duration-300 group-hover:scale-110 group-hover:bg-white group-hover:text-accent group-hover:ring-accent/40">
                          {i + 1}
                        </span>
                      </div>
                      <div className="mt-5 flex flex-1 flex-col p-4 transition-all duration-500 group-hover:-translate-y-1">
                        <h3 className="text-[15px] font-bold text-white text-center leading-tight whitespace-nowrap transition-colors duration-300 group-hover:text-accent-soft">{s.title}</h3>
                        <div className="mx-auto my-2 h-px w-10 bg-white/30 transition-all duration-300 group-hover:w-16 group-hover:bg-accent/70" />
                        <p className="text-[13px] leading-[1.45] text-white/80 text-center [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] overflow-hidden">{s.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Mobile/tablet — single horizontal swipeable timeline (1 row of 6) */}
            <div className="mt-12 lg:hidden">
              <div className="relative">
                {/* Animated dashed line */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-4 right-4 top-[44px] h-[2px] bg-[length:12px_2px] bg-repeat-x opacity-50"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, hsl(0 0% 100%) 50%, transparent 50%)",
                  }}
                />
                {/* Glowing flowing pulse */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-4 right-4 top-[42px] h-1 overflow-hidden rounded-full"
                >
                  <div className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-sm animate-[flow_4s_linear_infinite]" />
                </div>

                <ol
                  className="relative flex gap-4 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pt-4 pb-4 pl-4 pr-6 -mx-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {PROCESS_STEPS.map((s, i) => (
                    <li
                      key={s.title}
                      className="reveal group flex flex-col snap-center shrink-0 basis-[44%] sm:basis-[30%] md:basis-[22%]"
                      style={{ transitionDelay: `${i * 80}ms` }}
                    >
                      <div className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white border-2 border-white text-primary shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:bg-accent group-hover:border-accent group-hover:text-white group-hover:shadow-glow-green">
                        <s.icon className="h-5 w-5" />
                        <span className="absolute -top-2 -right-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent text-[11px] font-bold text-white shadow-md ring-2 ring-white/30 transition-all duration-300 group-hover:bg-white group-hover:text-accent group-hover:ring-accent/40">
                          {i + 1}
                        </span>
                      </div>
                      <div className="mt-5 flex flex-1 flex-col p-3 transition-all duration-500 group-hover:-translate-y-1">
                        {/* Plain element: the desktop timeline above already exposes these as h3 headings. */}
                        <div className="text-[13px] font-bold text-white text-center leading-tight whitespace-nowrap transition-colors duration-300 group-hover:text-accent-soft">{s.title}</div>
                        <div className="mx-auto my-1.5 h-px w-10 bg-white/30 transition-all duration-300 group-hover:w-14 group-hover:bg-accent/70" />
                        <p className="text-[12px] leading-snug text-white/80 text-center [display:-webkit-box] [-webkit-line-clamp:3] [-webkit-box-orient:vertical] overflow-hidden">{s.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                {/* Swipe hint */}
                <div className="mt-4 flex justify-center">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85 backdrop-blur-sm shadow-[0_4px_18px_-6px_hsl(0_0%_0%/0.4)]">
                    <ChevronLeft className="h-3.5 w-3.5 animate-[swipe-hint_1.6s_ease-in-out_infinite] text-accent" aria-hidden />
                    <span>Swipe to explore</span>
                    <ChevronRight className="h-3.5 w-3.5 animate-[swipe-hint_1.6s_ease-in-out_infinite_reverse] text-accent" aria-hidden />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ 6. PERFORMANCE AT SCALE ============ */}
        <section className="relative overflow-hidden border-y border-[hsl(var(--primary)/0.15)] bg-[hsl(var(--soft-bg))] py-20 md:py-28">
          <div aria-hidden className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

          <div className="container-cws relative">
            <div className="max-w-3xl reveal">
              <Eyebrow>Performance At Scale</Eyebrow>
              <h2
                className="mt-3 whitespace-nowrap font-extrabold tracking-tight text-charcoal"
                style={{ fontSize: "clamp(1.5rem, 1rem + 1.6vw, 2.5rem)", lineHeight: 1.08, letterSpacing: "-0.018em" }}
              >
                Operational Impact
              </h2>
              <SectionLead>
                Reliable service, strong recovery performance, and long-term environmental impact.
              </SectionLead>
            </div>

            {(() => {
              const STATS = [
                { icon: Users, value: 343000, suffix: "+", label: "U.S. Customers Served", sub: "Across Bay Area service areas", speed: 4200, range: 42 },
                { icon: Recycle, value: 717600, suffix: "+", label: "Tons Processed Annually", sub: "Bay Area operations", speed: 3600, range: 88 },
                { icon: Leaf, value: 341, suffix: "", label: "Acres at Da Phuoc Facility", sub: "Long-term infrastructure", speed: 5200, range: 12 },
                { icon: Factory, value: 3000000, suffix: "+", label: "Tons Managed Across Operations", sub: "Lifetime impact", speed: 3000, range: 120 },
              ];
              const highlight = STATS[3];
              const supporting = STATS.filter((s) => s.label !== highlight.label);
              return (
                <div className="reveal group mt-12 overflow-hidden rounded-[2rem] border border-border bg-card shadow-[0_30px_90px_-50px_hsl(var(--primary-deep)/0.65)]">
                  <div className="relative grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
                    {/* Highlight panel — video background */}
                    <div className="relative overflow-hidden lg:rounded-l-[2rem]">
                      {/* Video background */}
                      <VideoHeroBackground
                        src="/__l5e/assets-v1/76a451cb-ca62-49e3-83d0-a238ac8165f7/da-phuoc-facility.mp4"
                        poster="/videos/da-phuoc-facility-poster.webp"
                        posterAlt="Heavy equipment working across a large landfill and resource recovery site with dense green tree cover and a distant city skyline"
                      />
                      {/* Blue editorial overlay to preserve readability */}
                      <div
                        aria-hidden
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(180deg, hsl(var(--primary-deep) / 0.82) 0%, hsl(var(--primary) / 0.65) 50%, hsl(var(--primary-deep) / 0.88) 100%)",
                        }}
                      />
                      <div aria-hidden className="absolute inset-0 bg-grid-faint opacity-20" />

                      <div className="relative flex min-h-[420px] flex-col justify-between gap-8 p-6 md:p-10">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary-foreground/15 ring-1 ring-primary-foreground/30 text-primary-foreground backdrop-blur-md transition-all duration-300 hover:scale-110 hover:bg-primary-foreground/25">
                            <highlight.icon className="h-6 w-6" />
                          </span>
                        </div>
                        <div>
                          <p className="max-w-sm text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/80">{highlight.sub}</p>
                          <p className="mt-4 text-[clamp(2.25rem,7.5vw,4.75rem)] font-black leading-[0.95] tracking-tight text-primary-foreground tabular-nums">
                            <CountUp end={highlight.value} suffix={highlight.suffix} loop loopRange={highlight.range} loopInterval={highlight.speed} />
                          </p>
                          <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-primary-foreground/15">
                            <span className="block h-full w-2/3 rounded-full bg-primary-foreground/85 shadow-[0_0_22px_hsl(var(--primary-foreground)/0.45)] animate-[pulse_4s_ease-in-out_infinite]" />
                          </div>
                          <p className="mt-4 max-w-md text-lg font-extrabold leading-tight text-primary-foreground md:text-xl">{highlight.label}</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative bg-card p-3 md:p-5">
                      <div className="flex h-full flex-col justify-center divide-y divide-border/70">
                        {supporting.map((s, i) => (
                          <article
                            key={s.label}
                            className="group/metric relative grid gap-4 px-3 py-5 transition-all duration-300 hover:bg-primary-soft/40 sm:grid-cols-[auto_minmax(0,1fr)] md:px-5"
                            style={{ transitionDelay: `${i * 60}ms` }}
                          >
                            <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--primary-soft))] text-primary transition-all duration-300 group-hover/metric:scale-110 group-hover/metric:bg-primary group-hover/metric:text-primary-foreground">
                              <s.icon className="h-6 w-6" />
                            </span>
                            <div className="min-w-0">
                              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{s.label}</p>
                              <p className="mt-1.5 text-[clamp(1.625rem,4.5vw,2.75rem)] font-black leading-none tracking-tight text-primary tabular-nums">
                                {s.label === "Acres at Da Phuoc Facility"
                                  ? `${s.value.toLocaleString()}${s.suffix}`
                                  : <CountUp end={s.value} suffix={s.suffix} loop loopRange={s.range} loopInterval={s.speed} />}
                              </p>
                              <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{s.sub}</p>
                            </div>
                          </article>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* ============ 7. RECYCLING GUIDE ============ */}
        <section className="relative overflow-hidden border-t border-border bg-gradient-to-b from-white via-[hsl(var(--soft-bg))] to-[hsl(var(--soft-bg))] pt-20 md:pt-28 pb-10 md:pb-14">
          {/* Decorative blurs */}
          <div aria-hidden className="pointer-events-none absolute -top-24 -left-20 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-24 -right-20 h-80 w-80 rounded-full bg-accent/15 blur-3xl" />

          <div className="container-cws relative">
            <div className="mx-auto max-w-3xl text-center reveal">
              <Eyebrow>Recycling Guide</Eyebrow>
              <SectionTitle>Know What Goes Where Before Pickup Day</SectionTitle>
              <SectionLead>
                Hover or tap any item to see if it belongs in your CWS recycling cart. Keep recyclables empty, dry, and loose, never bagged.
              </SectionLead>
              <div className="mt-6 flex justify-center">
                <Button asChild variant="cta" size="lg" className="group">
                  <Link to="/recycling-guide">
                    Open Full Recycling Guide
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Stage */}
            <RecyclingStage />


            {/* Helpful rules row */}
            <div className="mt-14 grid gap-4 sm:grid-cols-3">
              {[
                { icon: Droplet, title: "Empty & Dry", text: "Rinse containers; no liquids or food residue." },
                { icon: Recycle, title: "Loose, Not Bagged", text: "Please place items loose inside the cart, never in plastic bags." },
                { icon: ShieldCheck, title: "When in Doubt", text: "Leave it out. Contamination disrupts recycling." },
              ].map((g, i) => (
                <article
                  key={g.title}
                  className="reveal group rounded-2xl border border-border bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-card-hover"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-soft text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground">
                    <g.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-3 font-display text-[15px] font-bold text-charcoal">{g.title}</h3>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">{g.text}</p>
                </article>
              ))}
            </div>

          </div>
        </section>

        {/* ============ Latest published news (auto-pulled from CMS) ============ */}
        <HomepageNews />

        {/* ============ 8. NEWS AND PUBLIC INFORMATION ============ */}
        <section className="relative overflow-hidden bg-[hsl(var(--soft-bg))] py-20 md:py-28">
          <div aria-hidden className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />

          <div className="container-cws relative">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-2xl reveal">
                <Eyebrow>News and Public Information</Eyebrow>
                <SectionTitle>News, Notices, and Service Information</SectionTitle>
                <SectionLead>
                  Find company updates, service information, public notices, community news, and recycling education.
                </SectionLead>
              </div>
              <div className="reveal">
                <Button asChild variant="cta" className="group">
                  <Link to="/news">
                    View Latest Updates
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-12">
              {NEWS_CARDS.slice(0, 1).map((n) => (
                <Link
                  key={n.title}
                  to={n.href}
                  className="reveal group relative col-span-full overflow-hidden rounded-3xl border border-border bg-charcoal shadow-card transition-all duration-500 hover:-translate-y-1 hover:shadow-xl lg:col-span-7"
                >
                  <div className="relative aspect-[16/10] w-full overflow-hidden lg:aspect-auto lg:h-full lg:min-h-[460px]">
                    {n.video ? (
                      <video
                        src={n.video}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        disablePictureInPicture
                        disableRemotePlayback
                        onLoadedData={(e) => { (e.currentTarget as HTMLVideoElement).play().catch(() => {}); }}
                        onPause={(e) => {
                          const v = e.currentTarget as HTMLVideoElement;
                          if (!v.ended && !document.hidden) v.play().catch(() => {});
                        }}
                        onEnded={(e) => {
                          const v = e.currentTarget as HTMLVideoElement;
                          try { v.currentTime = 0; } catch {}
                          v.play().catch(() => {});
                        }}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <img width={1200} height={800} decoding="async"
                        src={n.image}
                        alt={n.alt || n.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                    <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-transparent" />
                    <div aria-hidden className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-80" />
                    <div className="absolute inset-x-0 bottom-0 p-6 md:p-9">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-accent-foreground shadow-lg">
                        <Sparkles className="h-3 w-3" /> Featured · {n.tag}
                      </span>
                      <h3 className="mt-4 max-w-xl font-display text-2xl font-extrabold leading-tight text-white md:text-3xl lg:text-4xl">
                        {n.title}
                      </h3>
                      <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-white/85 md:text-[15.5px]">
                        {n.text}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-[12.5px] font-bold uppercase tracking-[0.16em] text-white backdrop-blur-sm transition-all duration-300 group-hover:bg-white group-hover:text-primary">
                        Read story
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
              <div className="col-span-full flex flex-col gap-6 lg:col-span-5">
                {NEWS_CARDS.slice(1).map((n, i) => (
                  <Link
                    key={n.title}
                    to={n.href}
                    className="reveal group relative flex flex-1 overflow-hidden rounded-3xl border border-border bg-white shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-accent/40 hover:shadow-xl"
                    style={{ transitionDelay: `${(i + 1) * 80}ms` }}
                  >
                    <div className="relative w-2/5 shrink-0 overflow-hidden bg-charcoal sm:w-[44%]">
                      <img width={1200} height={800} decoding="async"
                        src={n.image}
                        alt={n.alt || n.title}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div aria-hidden className="absolute inset-0 bg-gradient-to-tr from-charcoal/40 via-transparent to-transparent" />
                    </div>
                    <div className="flex flex-1 flex-col justify-center p-5 md:p-6">
                      <span className="inline-flex w-fit items-center rounded-full bg-primary-soft px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-primary">
                        {n.tag}
                      </span>
                      <h3 className="mt-2.5 font-display text-[17px] font-extrabold leading-snug text-charcoal transition-colors duration-300 group-hover:text-primary md:text-lg">
                        {n.title}
                      </h3>
                      <div className="mt-2 h-px w-8 bg-accent/60 transition-all duration-500 group-hover:w-16" />
                      <p className="mt-3 line-clamp-2 text-[13.5px] leading-relaxed text-muted-foreground">
                        {n.text}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-[0.16em] text-primary">
                        Read more
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

      </div>
    </Layout>
  );
};

export default Index;
